import { useState } from 'react'
import { formatPercent, formatRub, type UnitChartPoint, type UnitChartStep } from '@/lib/unit-economics'

const HEIGHT = 200
const COLUMN = 100
const GREEN = '#1f7a3d'
const RED = '#b3261e'

export interface MarginBar {
  key: string
  label: string
  title: string
  bought: number
  soldFor: number
  margin: number
  cumulative: number
  withoutCost: number
  sold: number
}

function axisLabel(rubles: number): string {
  const size = Math.abs(rubles)
  if (size >= 1_000_000) return `${(rubles / 1_000_000).toLocaleString('ru-RU', { maximumFractionDigits: 1 })} млн`
  if (size >= 1_000) return `${Math.round(rubles / 1_000).toLocaleString('ru-RU')} тыс`
  return Math.round(rubles).toLocaleString('ru-RU')
}

function scale(values: readonly number[]): (value: number) => number {
  const top = Math.max(0, ...values)
  const bottom = Math.min(0, ...values)
  const span = top - bottom || 1
  return (value) => ((top - value) / span) * HEIGHT
}

/** Margin bars per period, bought and sold beside them, and the cumulative margin line on its own axis. */
export function MarginChart({ bars, selected, onSelect, testId }: {
  bars: readonly MarginBar[]
  selected?: string | null
  onSelect?: (key: string) => void
  testId: string
}) {
  const [hovered, setHovered] = useState<number | null>(null)
  if (bars.length === 0) return <p className="text-[13px] text-ink-muted">Нет данных за период</p>
  const y = scale(bars.flatMap((bar) => [bar.bought, bar.soldFor, bar.margin]))
  const line = scale(bars.map((bar) => bar.cumulative))
  const width = bars.length * COLUMN
  const point = hovered === null ? null : bars[hovered]
  const last = bars[bars.length - 1]
  const rect = (x: number, value: number) => ({ x, y: Math.min(y(value), y(0)), height: Math.max(Math.abs(y(value) - y(0)), value === 0 ? 0 : 1) })
  const columns = { gridTemplateColumns: `repeat(${bars.length}, minmax(0, 1fr))` }

  return (
    <div className="flex flex-col gap-2" data-testid={testId}>
      <div className="flex flex-wrap gap-[18px] text-[12px] text-ink-2">
        <span className="flex items-center gap-1.5"><span className="size-2.5 bg-[#1f7a3d]" aria-hidden="true" />Маржа</span>
        <span className="flex items-center gap-1.5"><span className="h-0.5 w-4 bg-ink" aria-hidden="true" />Маржа нарастающим итогом · {formatRub(last.cumulative)}</span>
        <span className="flex items-center gap-1.5"><span className="size-2.5 bg-[#cfc6b8]" aria-hidden="true" />Купили за</span>
        <span className="flex items-center gap-1.5"><span className="size-2.5 bg-gold/60" aria-hidden="true" />Продали за</span>
      </div>
      <div className="overflow-x-auto pt-2">
        <div className="flex min-w-[420px] gap-2">
          <div className="tab-nums relative w-12 shrink-0 text-right text-[10px] text-ink-faint" style={{ height: HEIGHT }} aria-hidden="true">
            {[0, 0.5, 1].map((fraction) => {
              const values = bars.flatMap((bar) => [bar.bought, bar.soldFor, bar.margin])
              const top = Math.max(0, ...values)
              const bottom = Math.min(0, ...values)
              const value = top - (top - bottom) * fraction
              return <span key={fraction} className="absolute right-0 -translate-y-1/2" style={{ top: HEIGHT * fraction }}>{axisLabel(value)}</span>
            })}
          </div>
          <div className="relative flex-1">
            <svg viewBox={`0 0 ${width} ${HEIGHT}`} preserveAspectRatio="none" className="block w-full" style={{ height: HEIGHT }} aria-hidden="true">
              <line x1={0} x2={width} y1={y(0)} y2={y(0)} stroke="currentColor" className="text-rule-strong" vectorEffect="non-scaling-stroke" />
              {bars.map((bar, index) => {
                const x = index * COLUMN
                const highlighted = selected === bar.key || hovered === index
                return (
                  <g key={bar.key}>
                    {highlighted ? <rect x={x} y={0} width={COLUMN} height={HEIGHT} fill="#c9a24a" opacity={0.12} /> : null}
                    <rect {...rect(x + 12, bar.bought)} width={22} fill="#cfc6b8" />
                    <rect {...rect(x + 38, bar.soldFor)} width={22} fill="#c9a24a" opacity={0.6} />
                    <rect {...rect(x + 64, bar.margin)} width={24} fill={bar.margin >= 0 ? GREEN : RED} />
                  </g>
                )
              })}
              <polyline
                points={bars.map((bar, index) => `${index * COLUMN + COLUMN / 2},${line(bar.cumulative)}`).join(' ')}
                fill="none"
                stroke="#1c1814"
                strokeWidth={2}
                vectorEffect="non-scaling-stroke"
              />
            </svg>
            <div className="absolute inset-0 grid" style={columns}>
              {bars.map((bar, index) => (
                <button
                  key={bar.key}
                  type="button"
                  aria-label={`${bar.title}: маржа ${formatRub(bar.margin)}`}
                  aria-pressed={selected === bar.key}
                  disabled={!onSelect}
                  onClick={() => onSelect?.(bar.key)}
                  onMouseEnter={() => setHovered(index)}
                  onMouseLeave={() => setHovered(null)}
                  onFocus={() => setHovered(index)}
                  onBlur={() => setHovered(null)}
                  className="h-full outline-none disabled:cursor-default"
                />
              ))}
            </div>
            <div className="grid text-center text-[10px] text-ink-muted" style={columns} aria-hidden="true">
              {bars.map((bar, index) => (
                <span key={bar.key} className={`truncate ${index % 7 !== 0 && index !== bars.length - 1 ? 'max-md:invisible' : ''} ${hovered === index ? 'font-semibold text-ink' : ''}`}>
                  {bars.length <= 16 || index % Math.ceil(bars.length / 12) === 0 ? bar.label : ''}
                </span>
              ))}
            </div>
            {point && hovered !== null ? (
              <div
                role="tooltip"
                className="pointer-events-none absolute top-1 z-10 flex w-[230px] flex-col gap-px rounded-[4px] bg-ink px-2.5 py-2 text-[11px] leading-normal text-ivory"
                style={{ left: `${((hovered + 0.5) / bars.length) * 100}%`, transform: `translateX(${hovered === 0 ? '-10%' : hovered === bars.length - 1 ? '-90%' : '-50%'})` }}
              >
                <span className="font-semibold text-gold-soft">{point.title}</span>
                <span className="tab-nums">Продано {point.sold}{point.withoutCost ? `, без себестоимости ${point.withoutCost}` : ''}</span>
                <span className="tab-nums">Купили за {formatRub(point.bought)}</span>
                <span className="tab-nums">Продали за {formatRub(point.soldFor)}</span>
                <span className="tab-nums">
                  Маржа {formatRub(point.margin)} · {formatPercent(point.bought > 0 ? point.margin / point.bought : null)} от закупки · {formatPercent(point.soldFor > 0 ? point.margin / point.soldFor : null)} от продажи
                </span>
                <span className="tab-nums">Нарастающим итогом {formatRub(point.cumulative)}</span>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}

function shortDate(isoDate: string): string {
  return `${Number(isoDate.slice(8, 10))}.${isoDate.slice(5, 7)}`
}

const MONTHS = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'] as const

export function monthLabel(month: string): string {
  return `${MONTHS[Number(month.slice(5, 7)) - 1]} ${month.slice(0, 4)}`
}

export const STEP_TITLES: Record<UnitChartStep, string> = { day: 'по дням', week: 'по неделям', month: 'по месяцам' }

export function periodBars(points: readonly UnitChartPoint[], step: UnitChartStep): MarginBar[] {
  return points.map((point) => ({
    key: point.from,
    label: step === 'month' ? monthLabel(point.from.slice(0, 7)) : shortDate(point.from),
    title: step === 'day' ? shortDate(point.from) : step === 'week' ? `Неделя ${shortDate(point.from)}–${shortDate(point.to)}` : monthLabel(point.from.slice(0, 7)),
    bought: point.bought,
    soldFor: point.soldFor,
    margin: point.margin,
    cumulative: point.cumulativeMargin,
    withoutCost: point.withoutCost,
    sold: point.sold,
  }))
}
